import {
  useState,
  useEffect,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { desktopBreakpoint } from 'breakpoints';
import Button from 'components/Button';
import type { CardProps } from 'components/Card';
import Helmet from 'components/Helmet';
import Loader from 'components/Loader';
import PackBattle from 'components/PackBattle';
import PopUp from 'components/PopUp';
import Promo from 'components/Promo';
import Share from 'components/Share';
import type { TextUnderCardProps } from 'components/TextUnderCard';
import { trackEvent } from 'global/firebase';
import { shareUrl } from 'helpers/shareMobileUrl';
import { numberForScore } from 'helpers/TextHelpers';
import { useWindowSize } from 'hooks/useWindowSize';
import { RouteTypes } from 'RouteTypes';
import { setActiveCard } from 'store/app/appActions';
import { selectAuthUserId } from 'store/auth/authSelectors';
import {
  collectionRequest,
  collectionStatsRequest,
  countersRequest,
  publishCollectionResetStatus,
  viewAllCards,
} from 'store/collection/collectionActions';
import { selectCollectionById, selectCollectionCounters, selectCollectionStatsById } from 'store/collection/collectionSelectors';
import { packBattleLeaderRequest } from 'store/pack/packActions';
import { selectPackLeaderByCollectionId } from 'store/pack/packSelectors';
import { userRankingScoreRequest } from 'store/ranking/rankingActions';
import type { RootState } from 'store/store';
import { userRequest } from 'store/user/userActions';
import { selectUserById } from 'store/user/userSelectors';

import CollectionSalesInfo from '../../components/CollectionSalesInfo';
import ComponentPortal from '../../components/ComponentPortal';
import { useNodeRef } from '../../hooks/useNodeRef';
import { isIosOrAndroidWithShare } from '../../util/isIosOrAndroidWithShare';
import CollectionUser from './components/CollectionUser/CollectionUser';
import CardCollection from '../Sale/components/CardCollection/CardCollection';
import CollectionInfo from '../Sale/components/CollectionInfo/CollectionInfo';
import PackList from '../Sale/components/Packs/PackList';
import PackPurchaseStatusPopup from './components/PackPurchaseStatusPopup';
import {
  ButtonWrapper,
  CollectionSalesInfoWrapper,
  CollectionWrapper,
  GroupBtn,
  LoaderContainer,
  ModalSuccessText,
} from './styled';
import TopCollectorsList from './components/TopCollectors';
import RewardsSection from './components/RewardsSection';

const CurrentCollection = ({
  match, lastCollectionId,
}: { match: any, lastCollectionId?: string }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const width = useWindowSize();
  const showPublishedMsg = useSelector((state: RootState) => state.collections.activeCollection.showPublishedMsg);
  const userId = useSelector(selectAuthUserId);
  const [shareLinkDesktop, setShareLinkDesktop] = useState(false);
  const [ended, setEnded] = useState(showPublishedMsg);
  const [packsNode, packsRef] = useNodeRef();
  const statsCollectionPromoRef = useRef(null);

  let collectionId: string;
  if (lastCollectionId) {
    collectionId = lastCollectionId;
  } else {
    collectionId = match.params.collectionId;
  }
  const { username } = match.params;

  const isDesktop = width >= desktopBreakpoint;

  const { viewedAllCards, viewedTopCollectors } = useSelector((state: RootState) => state.collections.itemsCachedState[collectionId] || {});
  const [isDesktopCard, setIsDesktopCard] = useState<boolean>(isDesktop || viewedAllCards);

  const collection = useSelector(selectCollectionById(collectionId));
  const collectionStats = useSelector(selectCollectionStatsById(collectionId));
  const creatorId = collection?.creator.id;
  const creatorUsername = collection?.creator.username;
  if (collection && username && username !== creatorUsername && !lastCollectionId) {
    history.replace(`/${creatorUsername}/${collectionId}`);
  }

  const user = useSelector(selectUserById(creatorId));

  const packBattle = useSelector(selectPackLeaderByCollectionId(collectionId));

  useEffect(() => {
    if (packsNode && window.location.hash === '#packs') {
      packsNode.scrollIntoView();
      window.scrollTo(window.scrollX, window.scrollY - 100);
    }
  }, [packsNode]);

  /**
   * Fetch user info and score by user id
   */
  useEffect(() => {
    if (creatorId) {
      dispatch(userRequest({ id: creatorId }));
      dispatch(userRankingScoreRequest({ id: creatorId }));
    }
  }, [creatorId]);

  useEffect(() => {
    if (collectionId) {
      dispatch(countersRequest({ id: collectionId }));
      dispatch(packBattleLeaderRequest({ collectionId }));
    }
    return () => {
      dispatch(setActiveCard(null));
    };
  }, []);

  useEffect(() => {
    if (showPublishedMsg) {
      setEnded(true);
    }
  }, [showPublishedMsg]);

  const [collectionCards, setCollectionCards] = useState<CardProps[]>();
  const [textForCards, setTextForCards] = useState<TextUnderCardProps[]>();

  /*
   * Get cards data
  */
  const collectionCounters = useSelector(selectCollectionCounters(collectionId));

  const handleShowCards = (isShowAllCards?: boolean) => {
    if (collection) {
      const cards = collection._cached_cards || [];
      const isDesktopCards = isDesktopCard || isShowAllCards ? cards : cards.slice(0, 10);
      setCollectionCards(isDesktopCards.map((item) => ({
        media: item.media,
        qrCodeUrl: item.qrcode,
        author: collection.creator.name,
        title: item.title,
        collection: collection.title,
        rarity: item.rarity,
        cardId: item.id,
        editions: item?.editions,
        totalEditions: item?.editions,
      })));
    }
  };

  useEffect(() => {
    if (collection && collectionCounters) {
      const cards = collection._cached_cards || [];
      setTextForCards(cards.map((card) => {
        const cardCounter = collectionCounters[card.id];
        return {
          listing: !!cardCounter?.for_sale,
          price: cardCounter?.lowest_price || 0,
          listingsCount: cardCounter?.listings || 0,
        };
      }));
    }
  }, [collection, collectionCounters]);

  useEffect(() => {
    if (collectionId) {
      dispatch(collectionRequest({
        id: collectionId,
        restoreTopCollectors: viewedTopCollectors,
      }));
      dispatch(collectionStatsRequest({ collectionId }));
    }
    if (collectionId && (collectionId !== collectionCounters.id)) {
      dispatch(countersRequest({ id: collectionId }));
    }
  }, [collectionId]);

  useEffect(() => {
    handleShowCards();
  }, [collection]);

  useEffect(() => {
    if (collection) {
      trackEvent('view_item', {
        items: [{
          item_id: collectionId,
          item_name: collection.title,
          item_category: 'Collection',
          quantity: 1,
        }],
      });
    }
  }, []);

  const releaseDate = collection?.sale_start_date?.seconds && new Date(collection?.sale_start_date?.seconds * 1000);
  const deadline = collection?.sale_end_date?.seconds && new Date(collection?.sale_end_date?.seconds * 1000);

  const [isSaleFinished, setIsSaleFinished] = useState<boolean>(deadline && (dayjs().diff(dayjs(deadline)) >= 0));
  const [isSaleStarted, setIsSaleStarted] = useState<boolean>(releaseDate && dayjs().diff(dayjs(releaseDate)) >= 0);

  useEffect(() => {
    if (releaseDate) {
      setIsSaleStarted(dayjs().diff(dayjs(releaseDate)) >= 0);
    }
  }, [releaseDate]);

  useEffect(() => {
    if (deadline) {
      setIsSaleFinished(dayjs().diff(dayjs(deadline)) >= 0);
    }
  }, [deadline]);

  const isCreatedCollection = !!(collection && collectionCards && textForCards);

  if (!isCreatedCollection) {
    return <LoaderContainer><Loader /></LoaderContainer>;
  }

  const closePublishMessage = () => {
    dispatch(publishCollectionResetStatus());
    setEnded(false);
  };

  const linkToCollection = RouteTypes.Collection.replace(':username', username).replace(':collectionId', collectionId);
  const collectionUrl = `${window.location.protocol}//${window.location.host}${linkToCollection}`;
  const shareHandle = () => {
    const url = collectionUrl;
    trackEvent('share_clicked');
    if (isIosOrAndroidWithShare() && !isDesktop) {
      shareUrl(url, undefined, undefined, () => {
        history.push(linkToCollection);
      });
    } else {
      closePublishMessage();
      setShareLinkDesktop(true);
    }
  };

  const handleAllCards = () => {
    setIsDesktopCard(!isDesktopCard);
    handleShowCards(!isDesktopCard);
    dispatch(viewAllCards({ id: collectionId, value: !isDesktopCard }));
  };

  const isPackBattleFinished = packBattle?.[0]?.packBattleEnded || false;

  const cardDistribution = collection.card_information.distribution;
  const { rarities } = collection.pack_information.distribution;
  const commonRareLegendaryDistinctCards = cardDistribution.common.distinct_cards
    + cardDistribution.rare.distinct_cards
    + cardDistribution.legendary.distinct_cards;
  return (
    <>
      <Helmet
        title={t('card.byAuthor', { title: collection.title, author: collection.creator.name, interpolation: { escapeValue: false } })}
        description={collection.description}
        image={collection.cover_media?.sizes[0].url || collection.cover_media?.sizes[0].screenshot}
      />
      <CollectionWrapper>
        <Promo
          headline={collection?.title}
          subline={collection.creator.name && t('collection.promo.subline', { creator: collection.creator.name })}
          cover={collection?.cover_media}
          deadline={deadline}
          releaseDate={releaseDate}
          isSaleFinished={isSaleFinished}
          isSaleStarted={isSaleStarted}
          shareHandle={shareHandle}
          editHandle={() => history.push(`/edit-collection/${collectionId}`)}
          isOwnCollection={collection?.creator?.id === userId}
          setIsSaleFinished={setIsSaleFinished}
          promoGradient={collection?.options?.cover_media_gradient}
          statsContainerRef={statsCollectionPromoRef}
        />
        {
          isSaleFinished ? (
            collectionStats && (
              <ComponentPortal pureRender={!isDesktop} element={statsCollectionPromoRef.current}>
                <CollectionSalesInfoWrapper>
                  <CollectionSalesInfo
                    inCirculation={collectionStats.cards_in_circulation}
                    volumeTraded={collectionStats.sales}
                    uniqueOwners={collectionStats.owners}
                    topSale={collectionStats.top_sale}
                  />
                </CollectionSalesInfoWrapper>
              </ComponentPortal>
            )
          ) : (
            <PackList
              id={collection.id}
              $refCallback={packsRef}
              pricing={collection.pack_information.pricing}
              cardDistribution={collection.pack_information.distribution}
              packCounter={collectionCounters}
              creator={collection.creator.name}
              title={collection.title}
              isSaleFinished={isSaleFinished}
              rarities={rarities}
              isSaleStarted={isSaleStarted}
              releaseDate={releaseDate}
              packDistribution={collection.pack_information.distribution}
              setIsSaleStarted={setIsSaleStarted}
            />
          )
        }

        {!isSaleFinished && !isPackBattleFinished && (
          <PackBattle
            copy="Each pack participates in the battle. Pack with the highest total card score wins.
             Winner is determined 60 minutes after the end of the primary sale of this collection."
            prize="Prize: 10,000 $STARLY"
            counter={packBattle?.length > 1 ? packBattle.length : undefined}
            leader={packBattle?.length ? packBattle.map((item, index) => (
              [
                <Link key={item?.user?.username} to={`/${item?.user?.username}`} target="_blank" title={item?.user?.username}>
                  @
                  {item?.user?.username}
                </Link>,
                index < packBattle.length - 1 ? ', ' : '',
              ]
            )) : ''}
            avatar={packBattle?.[0]?.user.avatar}
            highscore={packBattle?.[0]?.score ? numberForScore(packBattle?.[0]?.score, 2) : ''}
          />
        )}

        <div data-test-id="card_collection_wrapper">
          <CardCollection
            cards={collectionCards}
            cardInformation={collection.card_information}
            creatorUsername={collection.creator.username}
            ownerUsername={user?.username}
            textForCards={textForCards}
            collectionId={collection?.id}
          />
          {
            !isDesktopCard && collectionCards.length > 8 ? (
              <ButtonWrapper>
                <Button
                  size="regular"
                  fluid
                  background="transparent"
                  border="primary"
                  textColor="primary"
                  onClick={handleAllCards}
                >
                  {t('collection.viewAllCards', { cards: commonRareLegendaryDistinctCards })}
                </Button>
              </ButtonWrapper>
            ) : null
          }
        </div>

        <PackPurchaseStatusPopup />
        {ended && (
          <PopUp onHide={closePublishMessage}>
            {isSaleStarted ? (
              <ModalSuccessText>
                {t('collection.sale.started')}
              </ModalSuccessText>
            ) : (
              <ModalSuccessText>
                {t('collection.sale.countdown')}
              </ModalSuccessText>
            )}
            <GroupBtn width="100%" background="primary" onClick={shareHandle}>
              {t('collection.sale.shareLink')}
            </GroupBtn>
            <Button onClick={closePublishMessage} background="black" width="100%">
              {t('common.close')}
            </Button>
          </PopUp>
        )}
        <RewardsSection collectionId={collectionId} collection={collection} />
        {isSaleFinished && packBattle?.[0] && (
          <PackBattle
            copy="Each pack participates in the battle.Pack with the highest total card score wins.
              Winner is determined 60 minutes after the end of the primary sale of this collection."
            prize="Prize: 10,000 $STARLY"
            counter={packBattle?.length > 1 ? packBattle.length : undefined}
            leader={packBattle?.length ? packBattle.map((item, index) => (
              [
                <Link key={item?.user?.username} to={`/${item?.user?.username}`} target="_blank" title={item?.user?.username}>
                  @
                  {item?.user?.username}
                </Link>,
                index < packBattle.length - 1 ? ', ' : '',
              ]
            )) : ''}
            avatar={packBattle?.[0].user.avatar}
            highscore={numberForScore(packBattle?.[0].score, 2)}
            ended={isPackBattleFinished}
          />
        )}
        <TopCollectorsList collectionId={collectionId} />
      </CollectionWrapper>
      <CollectionInfo
        aboutCollection={collection.description}
        additionalDescription={collection.additional_description}
        releaseDate={collection.sale_start_date && dayjs(collection.sale_start_date?.seconds * 1000).format('MMMM D, YYYY')}
        promoVideoLink={collection.promo_video_url}
        descriptionAlignment={collection.options?.description_alignment}
      />
      {
        user && (
          <CollectionUser
            user={user}
            collectionId={collectionId}
            isOwnProfile={creatorId === userId}
          />
        )
      }
      {shareLinkDesktop && <Share url={collectionUrl} type="collection" onHide={() => setShareLinkDesktop(false)} />}
    </>
  );
};

export default CurrentCollection;
